'use client';

import { Suspense, useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';

import { ProfileCard } from '@/components/profile-card';
import { CollectionsIcon, CommentsIcon, WorksIcon } from '@/components/icons';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FollowUserButton } from '@/features/notifications/components/follow-user-button';
import { MessageUserButton } from '@/features/messages/components/message-user-button';
import type { UserProfileData } from '../types/author.types';
import type { AuthorWorksListResult } from '../types/author-works.types';
import { AuthorWorks } from '@/components/author-works';
import { DonationPaymentReturnHandler } from '@/features/donations/components/donation-payment-return-handler';
import { ProfileImageCropDialog } from '@/features/profile/components/profile-image-crop-dialog';
import { PROFILE_BANNER_CROP_ASPECT } from '@/features/profile/constants/image-crop';
import { useProfileImageCrop } from '@/features/profile/hooks/use-profile-image-crop';
import { useAvatar } from '@/hooks/use-avatar';

interface AuthorPageContentProps {
  userData: UserProfileData | null;
  initialWorksData: AuthorWorksListResult;
  userId: string;
  isOwnProfile: boolean;
}

// Transform UserProfileData to ProfileCard compatible format
const transformToProfileData = (userData: UserProfileData) => ({
  ...userData,
  donation: userData.donation
    ? {
        donationUserId: userData.donation.donationUserId || null,
        hasCard: userData.donation.hasCard ?? false,
      }
    : undefined,
});

export const AuthorPageContent = ({
  userData,
  initialWorksData,
  userId,
  isOwnProfile,
}: AuthorPageContentProps) => {
  const router = useRouter();
  const { uploadBanner, isLoading: isBannerLoading } = useAvatar();
  const {
    imageSrc: bannerImageSrc,
    isOpen: isBannerCropOpen,
    openWithFile: openBannerCrop,
    close: closeBannerCrop,
    onOpenChange: onBannerCropOpenChange,
    toCroppedFile: toBannerCroppedFile,
  } = useProfileImageCrop();
  // Count must come from the works listing, not user.fanficsCount — those sources drift.
  const [worksCount, setWorksCount] = useState(initialWorksData.totalDocs);

  const handleBannerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      openBannerCrop(file);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleBannerCropConfirm = useCallback(
    async (blob: Blob) => {
      const file = toBannerCroppedFile(blob);
      const success = await uploadBanner(file);
      if (success) {
        closeBannerCrop();
        router.refresh();
      }
    },
    [closeBannerCrop, router, toBannerCroppedFile, uploadBanner],
  );

  // Handle error state
  if (!userData) {
    return (
      <div className="bg-background dark:bg-[#1B1D22] min-h-screen">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Card className="p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground dark:text-white mb-4">
              Профиль не найден
            </h2>
            <p className="text-muted-foreground dark:text-[#898989] mb-4">
              Пользователь с указанным ID не найден
            </p>
            <Button onClick={() => window.history.back()}>Вернуться назад</Button>
          </Card>
        </div>
      </div>
    );
  }

  const profileData = transformToProfileData(userData);

  const stats = [
    {
      icon: CollectionsIcon,
      title: 'Библиотека',
      value: userData.collectionsCount?.toString() || '0',
    },
    {
      icon: WorksIcon,
      title: 'Работы',
      value: worksCount.toString(),
    },
    {
      icon: CommentsIcon,
      title: 'Комментарии',
      value: userData.commentsCount?.toString() || '0',
    },
  ];

  return (
    <div className="bg-background dark:bg-[#1B1D22] min-h-screen">
      <ProfileCard
        userData={profileData}
        isLoadingUser={false}
        isOwnProfile={isOwnProfile}
        onBannerChange={isOwnProfile ? handleBannerChange : undefined}
        isBannerLoading={isBannerLoading}
        stats={stats}
        profileActions={
          !isOwnProfile ? (
            <>
              <FollowUserButton userId={userId} />
              <MessageUserButton userId={userId} />
            </>
          ) : undefined
        }
      >
        <AuthorWorks
          key={`${userId}-${isOwnProfile ? 'own' : 'public'}`}
          userId={userId}
          authorName={userData.name || userData.username}
          totalWorksCount={worksCount}
          onTotalWorksCountChange={setWorksCount}
          initialFanfics={initialWorksData.fanfics}
          initialHasMore={initialWorksData.hasNextPage}
          initialLoadFailed={initialWorksData.failed}
        />
      </ProfileCard>
      {bannerImageSrc ? (
        <ProfileImageCropDialog
          open={isBannerCropOpen}
          imageSrc={bannerImageSrc}
          variant="banner"
          aspect={PROFILE_BANNER_CROP_ASPECT}
          isSubmitting={isBannerLoading}
          onOpenChange={onBannerCropOpenChange}
          onConfirm={handleBannerCropConfirm}
        />
      ) : null}
      <Suspense fallback={null}>
        <DonationPaymentReturnHandler />
      </Suspense>
    </div>
  );
};
